import { observable, makeObservable } from 'mobx'
import * as R from 'ramda'
import i18n from 'i18next'
import {
    DIRECTOR_USER,
    REPORT_USER,
} from '../../utils/Enums/UserRoles'

class SurveyStore {
    @observable filteredList = []

    @observable showCertification = false

    statuses = {
        open: true,
        inDevelop: true,
        closed: true,
        archived: false,
    }

    maxbiStatuses = {
        completed: true,
        inprogress: true,
        incomplete: true,
        archived: false,
    }

    surveys = []

    surveyTypes = {}

    respondentTypes = {}

    years = [{
        value: '',
        label: 'All',
    }]

    year = ''

    products = [{
        value: '',
        label: 'All',
    }]

    product = ''

    // isMaxbi is `true` on MaxbiSearch page
    isMaxbi = false

    declinedRolesForNavTabs = new RegExp(`${DIRECTOR_USER}|${REPORT_USER}`, 'gi')

    constructor(store, showCertification) {
        makeObservable(this)
        this.appStore = store
        this.showCertification = showCertification
        this.isTemplate = this.appStore.survey.metadata?.productInfo?.status === 5
        this.isParent = this.appStore.survey.metadata?.isParent
    }

    getSurveyData = async (url) => {
        const surveys = await this.appStore.http.get(url)
        return R.sortWith([
            R.descend(R.prop('closeDate')),
        ])(surveys)
    }

    getProductData = async () => {
        const products = await this.appStore.http.get('/product')

        return this.products.concat(R.sortWith([
            R.ascend(
                R.compose(
                    R.toLower,
                    R.prop('label'),
                ),
            ),
        ])(products.map(({ productId, name, edition }) => ({
            value: productId,
            label: `${name}${edition ? ` ${edition}` : ''}`,
        }))))
    }

    getSurveyRespondentTypesData = async () => {
        const surveyRespondentTypes = await this.appStore.http.get('/surveyrespondenttypes')

        const respondentTypes = surveyRespondentTypes.respondentTypes.reduce((map,
            { respondentType, respondentTypeName }) => {
            map[respondentType] = {
                selected: true,
                name: respondentTypeName,
            }
            return map
        }, {})

        const surveyTypes = surveyRespondentTypes.surveyTypes.reduce((map,
            { surveyType, surveyTypeName }) => {
            map[surveyType] = {
                selected: true,
                name: surveyTypeName,
            }
            return map
        }, {})

        return [respondentTypes, surveyTypes]
    }

    getYears = (surveys) => {
        this.years = this.years.concat(Object.keys(
            surveys
                .reduce((years, { closeDate }) => {
                    const surveyYear = new Date(closeDate).getFullYear()
                    if (!Object.hasOwnProperty.call(years, surveyYear)) {
                        years[surveyYear] = surveyYear
                    }
                    return years
                }, {}),
        ).map((year) => ({
            value: year,
            label: year,
        })))
    }

    getSurveys = async () => {
        const { isMaxbi } = this

        if (isMaxbi) {
            this.surveys = await this.getSurveyData('/maxbi')
        } else {
            [this.surveys, [this.respondentTypes, this.surveyTypes]] = await Promise.all([
                this.surveys = this.getSurveyData('/survey'),
                this.getSurveyRespondentTypesData(),
            ])
        }
        this.products = await this.getProductData()
        this.getYears(this.surveys)
        this.search()
    }

    toggleStatus = ({ value, name }) => {
        console.log(`toggleStatus ${value}, ${name}`)
        this.statuses[name] = value
        this.search()
    }

    toggleSurveyType = ({ value, name }) => {
        console.log(`toggleSurveyType ${value}, ${name}`)
        this.surveyTypes[name].selected = value
        this.search()
    }

    toggleRespondentType = ({ value, name }) => {
        console.log(`toggleRespondentType ${value}, ${name}`)
        this.respondentTypes[name].selected = value
        this.search()
    }

    changeProduct = ({ value }) => {
        this.product = value
        this.search()
    }

    changeYear = ({ value }) => {
        this.year = R.isEmpty(value)
            ? value
            : parseInt(value, 10)
        this.search()
    }

    search = () => {
        const {
            surveys, statuses, surveyTypes, respondentTypes, isMaxbi, maxbiStatuses,
        } = this
        const { searchStr } = this.searchModule

        const activeFiltered = surveys
            .filter(({ status }) => (isMaxbi
                ? (
                    maxbiStatuses[status]
                ) : (
                    (status === 1 && statuses.open)
                || (status === 2 && statuses.inDevelop)
                || (status === 3 && statuses.closed)
                || (status === 4 && statuses.archived)
                )))
            .filter(({ surveyType, respondentType }) => (
                isMaxbi
                || (
                    R.pathOr(false, [surveyType, 'selected'])(surveyTypes)
                    && R.pathOr(false, [respondentType, 'selected'])(respondentTypes)
                )
            ))
            .filter(({ productId }) => (
                R.isEmpty(this.product) || this.product === productId
            ))
            .filter(({ closeDate }) => (
                R.isEmpty(this.year) || this.year === new Date(closeDate).getFullYear()
            ))

        if (searchStr === '') {
            this.filteredList = activeFiltered
        } else if (!Number.isNaN(parseInt(searchStr, 10))) {
            this.filteredList = activeFiltered
                .filter((d) => d.productId === parseInt(searchStr, 10))
        } else {
            const re = new RegExp(searchStr, 'i')
            this.filteredList = activeFiltered
                .filter((d) => (
                    d.surveyName.match(re)
                        || d.surveyId.match(re)
                        || d.productName.match(re)
                ))
        }
    }

    toggleMaxbiStatus = ({ value, name }) => {
        this.maxbiStatuses[name] = value
        this.search()
    }

    showStylingAndBuilderTab = () => {
        const { roles } = this.appStore.tokenData
        const { surveyType } = this.appStore.survey.metadata.taxonomy
        return !roles.match(this.declinedRolesForNavTabs) && (surveyType !== 'EM' || this.isTemplate)
    }

    showDashboardTab = () => {
        const { surveyType } = this.appStore.survey.metadata.taxonomy
        return surveyType !== 'GE' && surveyType !== 'EM' && !this.isTemplate
    }

    showDistributeTab = () => {
        const { roles } = this.appStore.tokenData
        return !this.appStore.userData.flags.isDistributedDisabled
            && !roles.match(this.declinedRolesForNavTabs)
            && !this.isTemplate
            && !this.isParent
    }

    showRightMenuTab = () => {
        const { roles } = this.appStore.tokenData
        return !roles.match(this.declinedRolesForNavTabs)
    }

    // same as above, but settings is not on the "right" on new tab menu
    showSettingsAndPreview = () => {
        const { roles } = this.appStore.tokenData
        return !roles.match(this.declinedRolesForNavTabs)
    }

    showCertificationTab = () => {
        const { showCertification } = this

        return process.env.SHOW_CERTIFICATION && showCertification && !this.isTemplate
    }

    getNavigationTabs = (activeTab) => {
        const { survey } = this.appStore
        const { productId } = survey.metadata.productInfo
        const isNewTab = process.env.USE_NEW_HEADER_UI
        const tabs = ([
            {
                name: i18n.t('s_tab_surveybuilder'),
                icon: `${isNewTab ? 'fal fa-rectangle-history-circle-plus' : 'fal fa-gear'}`,
                link: `/survey/${survey.id}/setup`,
                style: `leftMenu ${activeTab === 'setup' ? 'active' : ''}`,
                visible: this.showStylingAndBuilderTab(),
                active: activeTab === 'setup',
            },
            {
                name: i18n.t('s_tab_styling'),
                icon: 'fa-light fa-swatchbook',
                link: `/survey/${survey.id}/preview`,
                style: `leftMenu ${activeTab === 'preview' ? 'active' : ''}`,
                visible: this.showStylingAndBuilderTab(),
                active: activeTab === 'preview',
            },
            {
                name: i18n.t('s_tab_schedule'),
                icon: 'fal fa-envelope',
                link: `/survey/${survey.id}/distribute`,
                style: `leftMenu ${['email', 'distribute', 'campaign', 'onsite', 'intercept', 'embed', 'qr'].includes(activeTab) ? 'active' : ''}`,
                visible: this.showDistributeTab(),
                active: ['email', 'distribute', 'campaign', 'onsite', 'intercept', 'embed', 'qr'].includes(activeTab),
            },
            {
                name: `${i18n.t('s_tab_schedule')} (V2)`,
                icon: 'fal fa-envelope',
                link: `/survey/${survey.id}/distributeV2`,
                style: `leftMenu ${['distributeV2'].includes(activeTab) ? 'active' : ''}`,
                visible: (this.showDistributeTab() && process.env.USE_NEW_DISTRIBUTION),
                active: activeTab === 'distributeV2',
            },
            {
                name: i18n.t('s_tab_reporting'),
                icon: 'fal fa-chart-bar',
                link: `/survey/${survey.id}/report`,
                style: `leftMenu ${['presentation', 'report'].includes(activeTab) ? 'active' : ''}`,
                visible: !this.isTemplate,
                active: ['presentation', 'report'].includes(activeTab),
            },
            {
                name: 'Dashboard',
                icon: 'fa-light fa-gauge-simple-max',
                link: `/survey/${survey.id}${this.appStore.getDashboardLink('product', productId)}`,
                style: `leftMenu ${activeTab === 'company' ? 'active' : ''}`,
                visible: this.showDashboardTab(),
                active: activeTab === 'company',
            },
            {
                name: 'Certification',
                icon: 'fa-light fa-badge-check',
                link: `/survey/${survey.id}/certification`,
                style: `leftMenu ${activeTab === 'certification' ? 'active' : ''}`,
                visible: this.showCertificationTab(),
                active: activeTab === 'certification',
            },
        ])
        if (isNewTab) {
            tabs.push({
                name: 'Settings',
                icon: 'fa-light fa-sliders',
                link: `/survey/${survey.id}/`,
                style: `leftMenu ${activeTab === undefined ? 'active' : ''}`,
                visible: this.showSettingsAndPreview(),
                active: activeTab === undefined,
            })
        }
        return tabs
    }
}

export default SurveyStore
